class CartProgress extends HTMLElement {
  constructor() {
    super();
    // Parse thresholds - handle both string and number inputs
    const giftThreshold = this.dataset.freeGiftThreshold;
    const shippingThreshold = this.dataset.freeShippingThreshold;
    
    this.freeGiftThreshold = giftThreshold ? parseFloat(giftThreshold) : 100;
    this.freeShippingThreshold = shippingThreshold ? parseFloat(shippingThreshold) : 150;
    
    // Ensure we have valid numbers
    if (isNaN(this.freeGiftThreshold)) this.freeGiftThreshold = 100;
    if (isNaN(this.freeShippingThreshold)) this.freeShippingThreshold = 150;
    
    this.freeGiftProductHandle = this.dataset.freeGiftProductHandle || '';
    this.freeGiftEnabled = this.dataset.freeGiftEnabled === 'true';
    this.freeShippingEnabled = this.dataset.freeShippingEnabled === 'true';
    this.freeGiftVariantId = null;
    this.cartUpdateUnsubscriber = undefined;
    this.isUpdatingCart = false;
  }

  connectedCallback() {
    // Subscribe to cart updates
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      this.handleCartUpdate(event);
    });

    // Initialize async operations
    this.initialize();
  }

  async initialize() {
    // Check for free gift variant ID first if needed
    if (this.freeGiftEnabled && this.freeGiftProductHandle) {
      await this.fetchFreeGiftVariantId();
      // After variant ID is loaded, check if we need to add/remove gift
      const cartData = await this.getCartData();
      if (cartData) {
        await this.handleCartUpdate({ cartData, source: 'initial-load' });
      }
    } else {
      // Initial update
      await this.updateProgress();
    }
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  async fetchFreeGiftVariantId() {
    if (!this.freeGiftProductHandle) {
      console.warn('Cart Progress: Free gift product handle is missing');
      return;
    }

    try {
      const response = await fetch(`/products/${this.freeGiftProductHandle}.js`);      
      if (!response.ok) {
        throw new Error(`Failed to fetch product: ${response.status}`);
      }
      
      const product = await response.json();
      if (product && product.variants && product.variants.length > 0) {
        // Store as number to ensure consistent comparison
        this.freeGiftVariantId = Number(product.variants[0].id);
        console.log('Cart Progress: Free gift variant ID loaded:', this.freeGiftVariantId);
      } else {
        console.warn('Cart Progress: No variants found for free gift product');
      }
    } catch (error) {
      console.error('Cart Progress: Error fetching free gift product:', error);
    }
  }

  async getCartData() {
    try {
      const response = await fetch(`${routes.cart_url}.js`);
      const cartData = await response.json();
      return cartData;
    } catch (error) {
      console.error('Error fetching cart data:', error);
      return null;
    }
  }

  async handleCartUpdate(event) {
    // Prevent infinite loops - ignore updates from this component
    // But allow initial-load to process
    if (event.source === 'cart-progress' || (this.isUpdatingCart && event.source !== 'initial-load')) {
      // Still update progress even if we triggered the update
      if (event.source === 'cart-progress' && event.cartData) {
        this.updateProgress(event.cartData);
      }
      return;
    }
    
    const cartData = event.cartData || event;
    if (!cartData || !cartData.items) return;

    const cartItems = cartData.items || [];

    // Check if free gift is already in cart (with proper variant ID comparison)
    const freeGiftItem = this.findFreeGiftItem(cartItems);
    const hasFreeGift = !!freeGiftItem;

    // Calculate cart total EXCLUDING the free gift to prevent loops
    // This ensures we check the threshold based on actual customer purchases
    let cartTotalWithoutGift = (cartData.subtotal_price || cartData.total_price || 0) / 100;
    
    if (hasFreeGift && freeGiftItem.final_line_price) {
      // Subtract the free gift price from total
      const freeGiftPrice = (freeGiftItem.final_line_price || freeGiftItem.line_price || 0) / 100;
      cartTotalWithoutGift = Math.max(0, cartTotalWithoutGift - freeGiftPrice);
    }

    // Determine if we should add or remove free gift
    if (this.freeGiftEnabled && this.freeGiftVariantId) {
      if (cartTotalWithoutGift >= this.freeGiftThreshold && !hasFreeGift) {
        console.log('Cart Progress: Adding free gift (cart total:', cartTotalWithoutGift, 'threshold:', this.freeGiftThreshold),')';
        await this.addFreeGift();
      } else if (cartTotalWithoutGift < this.freeGiftThreshold && hasFreeGift) {
        console.log('Cart Progress: Removing free gift (cart total:', cartTotalWithoutGift, 'threshold:', this.freeGiftThreshold),')';
        await this.removeFreeGift(cartItems, freeGiftItem);
      }
    }

    // Update progress bar (will calculate total excluding gift internally)
    await this.updateProgress(cartData);
  }

  findFreeGiftItem(cartItems) {
    if (!this.freeGiftEnabled || !this.freeGiftVariantId || !cartItems || cartItems.length === 0) {
      return null;
    }

    // Compare variant IDs (handle both string and number formats)
    return cartItems.find(item => {
      const itemVariantId = Number(item.variant_id || item.variant?.id || 0);
      return itemVariantId === this.freeGiftVariantId;
    }) || null;
  }

  renderCartSections(parsedState) {
    if (!parsedState.sections) return;

    // Update cart drawer items (cart-drawer-items component)
    // This must be done by replacing the entire element, just like cart.js onCartUpdate()
    const cartDrawerItems = document.querySelector('cart-drawer-items');
    if (cartDrawerItems && parsedState.sections['cart-drawer']) {
      try {
        const sectionHTML = parsedState.sections['cart-drawer'];
        const html = new DOMParser().parseFromString(sectionHTML, 'text/html');
        
        // Find the cart-drawer-items element in the new HTML
        const newCartDrawerItems = html.querySelector('cart-drawer-items');
        if (newCartDrawerItems && cartDrawerItems) {
          // Import the node to ensure it can be properly inserted into the document
          const importedNode = document.importNode(newCartDrawerItems, true);
          // Replace the entire element (browser will auto-upgrade custom element)
          cartDrawerItems.replaceWith(importedNode);
        }
        
        // Also update the footer if present
        const newFooter = html.querySelector('.cart-drawer__footer');
        const existingFooter = document.querySelector('.cart-drawer__footer');
        if (newFooter && existingFooter) {
          const importedFooter = document.importNode(newFooter, true);
          existingFooter.replaceWith(importedFooter);
        }
      } catch (error) {
        console.error('Cart Progress: Error updating cart-drawer-items:', error);
        // Fallback: try triggering cart-drawer-items' onCartUpdate method
        if (cartDrawerItems && typeof cartDrawerItems.onCartUpdate === 'function') {
          cartDrawerItems.onCartUpdate();
        }
      }
    }

    // Update cart drawer wrapper (cart-drawer component) empty state
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      // Update empty state
      const drawerInnerEl = cartDrawer.querySelector('.drawer__inner');
      if (drawerInnerEl) {
        drawerInnerEl.classList.toggle('is-empty', parsedState.item_count === 0);
      }
      cartDrawer.classList.toggle('is-empty', parsedState.item_count === 0);
    }

    // Update cart page if present
    const cartItems = document.querySelector('cart-items');
    if (cartItems && cartItems.getSectionsToRender) {
      cartItems.getSectionsToRender().forEach((section) => {
        const sectionHTML = parsedState.sections[section.section];
        if (sectionHTML) {
          const sectionElement = document.getElementById(section.id);
          if (sectionElement) {
            const elementToReplace = section.selector
              ? sectionElement.querySelector(section.selector) || sectionElement
              : sectionElement;
            
            if (elementToReplace) {
              elementToReplace.innerHTML = this.getSectionInnerHTML(sectionHTML, section.selector);
            }
          }
        }
      });
      
      // Update empty state
      cartItems.classList.toggle('is-empty', parsedState.item_count === 0);
      const cartFooter = document.getElementById('main-cart-footer');
      if (cartFooter) {
        cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
      }
    }

    // Update cart icon bubble if present
    if (parsedState.sections['cart-icon-bubble']) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble) {
        const bubbleSection = cartIconBubble.querySelector('.shopify-section');
        if (bubbleSection) {
          const newBubble = this.getSectionDOM(parsedState.sections['cart-icon-bubble'], '.shopify-section');
          if (newBubble) {
            bubbleSection.innerHTML = newBubble.innerHTML;
          }
        } else {
          // If no .shopify-section wrapper, update the whole element
          const newBubble = this.getSectionDOM(parsedState.sections['cart-icon-bubble']);
          if (newBubble) {
            cartIconBubble.innerHTML = newBubble.innerHTML;
          }
        }
      }
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    try {
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const element = parsed.querySelector(selector);
      return element ? element.innerHTML : html;
    } catch (e) {
      console.error('Cart Progress: Error parsing section HTML:', e);
      return html;
    }
  }

  getSectionDOM(html, selector = '.shopify-section') {
    try {
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      return parsed.querySelector(selector);
    } catch (e) {
      console.error('Cart Progress: Error parsing section DOM:', e);
      return null;
    }
  }

  async addFreeGift() {
    if (!this.freeGiftVariantId || this.isUpdatingCart) {
      console.warn('Cart Progress: Cannot add free gift - variant ID missing or update in progress');
      return;
    }

    this.isUpdatingCart = true;
    try {
      const formData = new FormData();
      formData.append('id', this.freeGiftVariantId.toString());
      formData.append('quantity', '1');
      
      // Request sections to update - include both cart drawer and cart page sections
      const sectionsToRequest = [];
      
      // Get cart drawer sections (request section names, not element IDs)
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer) {
        sectionsToRequest.push('cart-drawer');
      } else {
        sectionsToRequest.push('cart-drawer');
      }
      
      // Get cart page sections (if on cart page)
      const cartItems = document.querySelector('cart-items');
      if (cartItems && cartItems.getSectionsToRender) {
        const cartPageSections = cartItems.getSectionsToRender();
        cartPageSections.forEach(s => {
          // Use section name (like 'main-cart-items', 'main-cart-footer')
          if (s.section && !sectionsToRequest.includes(s.section)) {
            sectionsToRequest.push(s.section);
          }
        });
      }
      
      // Always include cart-icon-bubble
      if (!sectionsToRequest.includes('cart-icon-bubble')) {
        sectionsToRequest.push('cart-icon-bubble');
      }
      
      formData.append('sections', sectionsToRequest.join(','));
      formData.append('sections_url', window.location.pathname);

      // Mirror Dawn's product-form request so Shopify returns JSON (not HTML)
      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
      config.body = formData;

      const response = await fetch(`${routes.cart_add_url}`, config);
      const responseJson = await response.json();
       
       console.log('Cart Progress: Response:', response);
       console.log('Cart Progress: Response JSON:', responseJson);

       if (!this.error)
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'product-form',
          productVariantId: formData.get('id'),
          cartData: response,
        }).then(() => {
          CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
        });
      this.error = false;
      const quickAddModal = this.closest('quick-add-modal');
      if (quickAddModal) {
        document.body.addEventListener(
          'modalClosed',
          () => {
            setTimeout(() => {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            });
          },
          { once: true }
        );
        quickAddModal.hide(true);
      } else {
        CartPerformance.measure("add:paint-updated-sections", () => {
          this.cart.renderContents(response);
        });
      }


     
    } catch (error) {
      console.error('Cart Progress: Error adding free gift:', error);
    } finally {
      // Small delay to prevent rapid re-triggering
      setTimeout(() => {
        this.isUpdatingCart = false;
      }, 500);
    }
  }

  async removeFreeGift(cartItems, freeGiftItem = null) {
    if (!this.freeGiftVariantId || this.isUpdatingCart) {
      console.warn('Cart Progress: Cannot remove free gift - variant ID missing or update in progress');
      return;
    }

    // Find the free gift item if not provided
    if (!freeGiftItem) {
      freeGiftItem = this.findFreeGiftItem(cartItems);
    }
    
    if (!freeGiftItem) {
      console.warn('Cart Progress: Free gift item not found in cart');
      return;
    }

    this.isUpdatingCart = true;
    try {
      // Shopify cart API uses 1-based line index
      // The line property should be available from cart.js response
      let lineIndex = freeGiftItem.line;
      
      // If line is not available, try to find it by index position
      if (!lineIndex) {
        const itemIndex = cartItems.indexOf(freeGiftItem);
        if (itemIndex !== -1) {
          lineIndex = itemIndex + 1; // 1-based
        } else {
          // Try to get from properties
          lineIndex = freeGiftItem.index || freeGiftItem.key;
        }
      }

      if (!lineIndex) {
        console.error('Cart Progress: Cannot determine line index for free gift removal');
        return;
      }

      const body = JSON.stringify({
        line: lineIndex,
        quantity: 0,
        sections: ['cart-drawer'],
        sections_url: window.location.pathname,
      });

      const response = await fetch(`${routes.cart_change_url}`, {
        ...fetchConfig(),
        body: body,
      });

      if (!response.ok) {
        throw new Error(`Cart change failed: ${response.status}`);
      }

      const responseText = await response.text();
      const parsedState = JSON.parse(responseText);

      if (parsedState.errors) {
        console.error('Cart Progress: Error removing free gift:', parsedState.errors);
        return;
      }

      console.log('Cart Progress: Free gift removed successfully');
      
      // Render sections if they're included in the response
      if (parsedState.sections) {
        this.renderCartSections(parsedState);
      }
      
      // Trigger cart update event
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'cart-progress',
        cartData: parsedState,
      });
    } catch (error) {
      console.error('Cart Progress: Error removing free gift:', error);
    } finally {
      // Small delay to prevent rapid re-triggering
      setTimeout(() => {
        this.isUpdatingCart = false;
      }, 500);
    }
  }

  async updateProgress(cartData = null) {
    // Fetch cart data if not provided
    if (!cartData) {
      cartData = await this.getCartData();
      if (!cartData) return;
    }

    const cartItems = cartData.items || [];
    
    // Calculate cart total EXCLUDING the free gift for threshold checks
    // This ensures progress, milestones, and messages are based on actual customer purchases
    // Use subtotal_price if available (before taxes/shipping), otherwise total_price
    let cartTotal = (cartData.subtotal_price || cartData.total_price || 0) / 100;
    
    // Check if free gift is in cart and subtract its price
    const freeGiftItem = this.findFreeGiftItem(cartItems);
    if (freeGiftItem && freeGiftItem.final_line_price) {
      const freeGiftPrice = (freeGiftItem.final_line_price || freeGiftItem.line_price || 0) / 100;
      cartTotal = Math.max(0, cartTotal - freeGiftPrice);
    }

    // Determine thresholds and find the next unachieved one
    const thresholds = [];
    if (this.freeGiftEnabled && this.freeGiftThreshold > 0) {
      thresholds.push(this.freeGiftThreshold);
    }
    if (this.freeShippingEnabled && this.freeShippingThreshold > 0) {
      thresholds.push(this.freeShippingThreshold);
    }
    
    if (thresholds.length === 0) return;

    // Sort thresholds to find the next unachieved threshold
    const sortedThresholds = [...thresholds].sort((a, b) => a - b);
    const nextThreshold = sortedThresholds.find(t => cartTotal < t) || Math.max(...thresholds);
    
    // Calculate progress based on the next threshold (or max if all achieved)
    // Ensure we don't divide by zero and cap at 100%
    const progressPercentage = nextThreshold > 0 
      ? Math.min((cartTotal / nextThreshold) * 100, 100) 
      : 100;
    const maxThreshold = Math.max(...thresholds); // Still needed for milestone positioning

    // Update progress bar fill
    const progressFill = this.querySelector('.cart-progress__fill');
    if (progressFill) {
      progressFill.style.setProperty('--progress', `${progressPercentage}%`);
    }

    // Update milestones
    const milestones = this.querySelectorAll('.cart-progress__milestone');
    milestones.forEach((milestone) => {
      const threshold = parseFloat(milestone.dataset.threshold);
      const positionPercentage = (threshold / maxThreshold) * 100;
      milestone.style.setProperty('--milestone-position', `${positionPercentage}%`);
      
      if (cartTotal >= threshold) {
        milestone.classList.add('cart-progress__milestone--achieved');
      } else {
        milestone.classList.remove('cart-progress__milestone--achieved');
      }
    });

    // Update message
    this.updateMessage(cartTotal);
  }

  updateMessage(cartTotal) {
    const messageText = this.querySelector('.cart-progress__text');
    if (!messageText) return;

    const giftReached = this.freeGiftEnabled && cartTotal >= this.freeGiftThreshold;
    const shippingReached = this.freeShippingEnabled && cartTotal >= this.freeShippingThreshold;

    let message = '';

    // Both thresholds reached
    if (giftReached && shippingReached) {
      message = `You've <b class='text-primary'>Unlocked Free Shipping</b> and a <b class='text-primary'>Free Gift!</b>`;
    } 
    // Only shipping reached (gift threshold must be lower, so gift should also be reached)
    // But handle edge case where gift might not be enabled or thresholds are inverted
    else if (shippingReached) {
      if (this.freeGiftEnabled && !giftReached) {
        // Edge case: shipping threshold is lower than gift threshold
        const remaining = this.freeGiftThreshold - cartTotal;
        message = `Add <b>${this.formatMoney(remaining)}</b> more to <b class="text-primary">Unlock a Free Gift</b>.`;
      } else {
        message = `You've <b class='text-primary'>Unlocked Free Shipping</b>!`;
      }
    } 
    // Only gift reached (need to reach shipping)
    else if (giftReached) {
      const remaining = this.freeShippingThreshold - cartTotal;
      message = `Add <b>${this.formatMoney(remaining)}</b> to your order to <b class="text-primary">Unlock Free Shipping</b>.`;
    } 
    // Neither reached - show message for the closer threshold
    else {
      // Determine which threshold is closer
      const giftRemaining = this.freeGiftEnabled ? this.freeGiftThreshold - cartTotal : Infinity;
      const shippingRemaining = this.freeShippingEnabled ? this.freeShippingThreshold - cartTotal : Infinity;

      if (giftRemaining <= shippingRemaining && this.freeGiftEnabled) {
        message = `Add <b>${this.formatMoney(giftRemaining)}</b> more to <b class="text-primary">Unlock a Free Gift</b>.`;
      } else if (this.freeShippingEnabled) {
        message = `Add <b>${this.formatMoney(shippingRemaining)}</b> to your order to <b class="text-primary">Unlock Free Shipping</b>.`;
      }
    }

    messageText.innerHTML = message;
  }

  formatMoney(amount) {
    if (amount <= 0) return '$0.00';
    
    // Convert to cents for Shopify formatMoney
    const amountInCents = Math.round(amount * 100);
    
    // Use Shopify's money format if available
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      const moneyFormat = window.Shopify?.money_format || '${{amount}}';
      return Shopify.formatMoney(amountInCents, moneyFormat);
    }
    
    // Fallback formatting using Intl
    try {
      const currency = window.Shopify?.currency?.active || 
                      document.documentElement.getAttribute('data-currency') || 
                      'USD';
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    } catch (e) {
      // Ultimate fallback
      return `$${amount.toFixed(2)}`;
    }
  }
}

customElements.define('cart-drawer-progress', CartProgress);



